import React, { Component } from 'react';
import styled, { ThemeProvider, DefaultTheme } from 'styled-components';

import FjTableRow from '../fj-table-row';

interface FjTableStyle {
  gridTemplateColumns?: string;
  width?: string;
  fontSize?: string;
  color?: string;
  bgColor?: string;
  borderColor?: string;
  rowHeight?: string;
  cellPadding?: string;
}

interface FjTableProps {
  style: FjTableStyle;
  children?: React.ReactNode;
}

// The theme shape provided to the styled-components below via ThemeProvider.
// It differs from the app-wide DefaultTheme, so theme access is cast to this.
interface FjTableTheme {
  gridTemplateColumns?: string;
  width: string;
  fontSize: string;
  color: string;
  bgColor: string;
  borderColor: string;
  rowHeight: string;
  cellPadding: string;
}

class FjTable extends Component<FjTableProps> {
  get styleWithDefaults(): FjTableTheme {
    return {
      gridTemplateColumns: this.props.style.gridTemplateColumns,
      width: this.props.style.width || '100%',
      fontSize: this.props.style.fontSize || '1em',
      color: this.props.style.color || 'currentColor',
      bgColor: this.props.style.bgColor || 'transparent',
      borderColor: this.props.style.borderColor || 'black',
      rowHeight: this.props.style.rowHeight || '1fr',
      cellPadding:
        this.props.style.cellPadding ?? (this.props.style.rowHeight ? '0px 15px' : '15px 15px'),
    };
  }

  // Only render FjTableRow children
  get validChildren(): React.ReactElement[] {
    const mapped = React.Children.map(this.props.children, (child) => {
      const element = child as React.ReactElement;
      if (
        element.type === FjTableRow ||
        (element.type as { prototype?: unknown })?.prototype instanceof FjTableRow
      ) {
        return element;
      }
      return null;
    });
    return (mapped ?? []).filter((child): child is React.ReactElement => child !== null);
  }

  renderTableRows(): React.ReactNode {
    return this.validChildren.map((row, index) => {
      const element = row as React.ReactElement<{ children?: React.ReactNode }>;
      const rowProps = {
        ...element.props,
        _rowIndex: index + 1,
        key: `row-${index + 1}`,
      };
      return React.cloneElement(element, rowProps, element.props.children);
    });
  }

  render(): React.ReactNode {
    return (
      <ThemeProvider theme={this.styleWithDefaults as unknown as DefaultTheme}>
        <TableContainer>{this.renderTableRows()}</TableContainer>
      </ThemeProvider>
    );
  }
}

export default FjTable;

const asTableTheme = (theme: DefaultTheme): FjTableTheme => theme as unknown as FjTableTheme;

const TableContainer = styled.div`
  display: grid;
  grid-template-columns: ${(props) => asTableTheme(props.theme).gridTemplateColumns};
  /* A row can place more cells than gridTemplateColumns declares (e.g. a label+value+detail row
     alongside label+value-only rows); those extra cells fall into implicit columns, which default
     to plain "auto" sizing - content-based, not wrap-safe. minmax(0, 1fr) lets an implicit column
     shrink to fit a constrained container instead of forcing the whole table to overflow it. */
  grid-auto-columns: minmax(0, 1fr);
  grid-auto-rows: ${(props) => asTableTheme(props.theme).rowHeight};
  color: ${(props) => asTableTheme(props.theme).color};
  background-color: ${(props) => asTableTheme(props.theme).bgColor};
  font-size: ${(props) => asTableTheme(props.theme).fontSize};
  width: ${(props) => asTableTheme(props.theme).width};
  border: solid 1px ${(props) => asTableTheme(props.theme).borderColor};
  border-radius: 3px;
`;
